/**
 * Fenix systems status: IDA availability from status API + maintenance modal.
 * Config: window.FENIX_SYSTEMS_STATUS (injected from PHP).
 */

const MODAL_ID = "fenix-ida-maintenance-modal";
const CACHE_KEY = "fenixSystemsStatusCache";
const DEFAULT_TTL_MS = 45000;
/** Seconds between automatic re-checks while the maintenance modal is open */
const RECHECK_INTERVAL_SECONDS = 120;

let maintenanceTickInterval = null;
let modalRecheckEventsBound = false;

function getConfig() {
  return window.FENIX_SYSTEMS_STATUS || {};
}

function getLangIsEn() {
  const lang =
    (document.documentElement && document.documentElement.lang) || "sv";
  return String(lang).toLowerCase().indexOf("en") === 0;
}

function defaultMessages() {
  const en = getLangIsEn();
  return {
    title: en
      ? "Service temporarily unavailable"
      : "Tjänsten är tillfälligt otillgänglig",
    text: en
      ? "We cannot process this request right now. Please try again later."
      : "Vi kan inte ta emot denna förfrågan just nu. Försök igen senare.",
  };
}

function parseServiceEntry(doc) {
  const services = doc && doc.services;
  if (!Array.isArray(services)) {
    return null;
  }
  const names = getConfig().ida_service_names || ["idaApi", "api"];
  for (let i = 0; i < services.length; i++) {
    const s = services[i];
    if (s && names.indexOf(s.serviceName) !== -1) {
      return s;
    }
  }
  return null;
}

function messagesFromEntry(entry) {
  const en = getLangIsEn();
  const def = defaultMessages();
  if (!entry) {
    return def;
  }
  let title;
  let text;
  if (en) {
    title = (entry.messageTitleEn || "").trim();
    text = (entry.messageTextEn || "").trim();
  } else {
    title = (entry.messageTitleSe || "").trim();
    text = (entry.messageTextSe || "").trim();
  }
  return {
    title: title || def.title,
    text: text || def.text,
  };
}

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) {
      return null;
    }
    const o = JSON.parse(raw);
    if (!o || typeof o.t !== "number" || !o.payload) {
      return null;
    }
    const ttl = getConfig().cache_ttl_ms || DEFAULT_TTL_MS;
    if (Date.now() - o.t > ttl) {
      return null;
    }
    return o.payload;
  } catch (e) {
    return null;
  }
}

function writeCache(payload) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ t: Date.now(), payload })
    );
  } catch (e) {}
}

function clearStatusCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch (e) {}
}

function formatCountdown(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m + ":" + (s < 10 ? "0" : "") + s;
}

function recheckCountdownLabel(seconds) {
  const en = getLangIsEn();
  const t = formatCountdown(seconds);
  return en
    ? "Checking again in: " + t
    : "Kontrollerar igen om: " + t;
}

function checkingLabel() {
  return getLangIsEn() ? "Checking…" : "Kontrollerar…";
}

function stopMaintenanceRecheck() {
  if (maintenanceTickInterval) {
    clearInterval(maintenanceTickInterval);
    maintenanceTickInterval = null;
  }
}

function runMaintenanceStatusRecheck() {
  const recheckEl = modalEl && modalEl.querySelector(".fenix-ida-maint-recheck");
  if (recheckEl) {
    recheckEl.textContent = checkingLabel();
  }
  return fetchSystemsStatus(true).then((doc) => {
    if (isIdaOkFromDoc(doc)) {
      document.body.classList.remove("fenix-ida-down");
      stopMaintenanceRecheck();
      if (window.jQuery && jQuery.fn.modal && modalEl) {
        jQuery(modalEl).modal("hide");
      }
      window.location.reload();
      return;
    }
    document.body.classList.add("fenix-ida-down");
    startMaintenanceCountdownLoop();
  });
}

function startMaintenanceCountdownLoop() {
  stopMaintenanceRecheck();
  if (!getConfig().status_url) {
    const recheckEl = modalEl && modalEl.querySelector(".fenix-ida-maint-recheck");
    if (recheckEl) {
      recheckEl.textContent = "";
    }
    return;
  }
  let remaining = RECHECK_INTERVAL_SECONDS;
  const recheckEl = modalEl && modalEl.querySelector(".fenix-ida-maint-recheck");
  const tick = () => {
    if (recheckEl) {
      recheckEl.textContent = recheckCountdownLabel(remaining);
    }
  };
  tick();
  maintenanceTickInterval = setInterval(() => {
    remaining--;
    if (remaining > 0) {
      tick();
    } else {
      clearInterval(maintenanceTickInterval);
      maintenanceTickInterval = null;
      runMaintenanceStatusRecheck();
    }
  }, 1000);
}

export function fetchSystemsStatus(force) {
  if (!force) {
    const cached = readCache();
    if (cached) {
      return Promise.resolve(cached);
    }
  } else {
    clearStatusCache();
  }
  const url = getConfig().status_url;
  if (!url) {
    return Promise.resolve({
      services: [{ serviceName: "idaApi", serviceStatus: "ok" }],
    });
  }
  return fetch(url, { credentials: "omit", cache: "no-store" })
    .then((r) => (r.ok ? r.json() : null))
    .then((doc) => {
      if (doc) {
        writeCache(doc);
      }
      return doc;
    })
    .catch(() => null);
}

export function isIdaOkFromDoc(doc) {
  if (!getConfig().status_url) {
    return true;
  }
  const entry = parseServiceEntry(doc);
  if (!entry) {
    return false;
  }
  return entry.serviceStatus === "ok";
}

export function isIdaOperational() {
  return fetchSystemsStatus().then((doc) => isIdaOkFromDoc(doc));
}

let modalEl = null;

function ensureModalElement() {
  if (modalEl && document.body.contains(modalEl)) {
    return modalEl;
  }
  const existing = document.getElementById(MODAL_ID);
  if (existing) {
    modalEl = existing;
    return modalEl;
  }
  const wrap = document.createElement("div");
  wrap.innerHTML = `
<div class="modal fade" id="${MODAL_ID}" tabindex="-1" role="dialog" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title fenix-ida-maint-title"></h5>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body fenix-ida-maint-body"></div>
      <p class="fenix-ida-maint-recheck px-3 mb-0 small text-muted" aria-live="polite"></p>
      <div class="modal-footer">
        <button type="button" class="btn btn-primary btn-blue" data-dismiss="modal">OK</button>
      </div>
    </div>
  </div>
</div>`;
  document.body.appendChild(wrap.firstElementChild);
  modalEl = document.getElementById(MODAL_ID);
  if (!modalRecheckEventsBound && window.jQuery && jQuery.fn.modal && modalEl) {
    modalRecheckEventsBound = true;
    jQuery(modalEl).on("hidden.bs.modal", function () {
      stopMaintenanceRecheck();
      const recheckEl = modalEl.querySelector(".fenix-ida-maint-recheck");
      if (recheckEl) {
        recheckEl.textContent = "";
      }
    });
  }
  return modalEl;
}

export function showIdaMaintenanceModal(title, text) {
  const el = ensureModalElement();
  el.querySelector(".fenix-ida-maint-title").textContent = title;
  el.querySelector(".fenix-ida-maint-body").textContent = text;
  if (window.jQuery && jQuery.fn.modal) {
    jQuery(modalEl).modal("show");
    startMaintenanceCountdownLoop();
  } else {
    el.style.display = "block";
    el.classList.add("show");
    startMaintenanceCountdownLoop();
  }
}

export function ensureIdaOkOrShowModal() {
  return fetchSystemsStatus().then((doc) => {
    if (isIdaOkFromDoc(doc)) {
      document.body.classList.remove("fenix-ida-down");
      return true;
    }
    document.body.classList.add("fenix-ida-down");
    const entry = parseServiceEntry(doc);
    const m = messagesFromEntry(entry);
    showIdaMaintenanceModal(m.title, m.text);
    return false;
  });
}

function closestOpenTarget(el, selector) {
  if (!el || !el.closest) {
    return null;
  }
  return el.closest(selector);
}

/**
 * Element that opens an IDA-backed booking/contact modal.
 * @returns {Element|null}
 */
function findInterceptedOpener(target) {
  if (!target || !target.closest) {
    return null;
  }
  return (
    closestOpenTarget(target, '[data-target="#reactForm"]') ||
    closestOpenTarget(target, '[data-target="#contactUsForm"]')
  );
}

export function attachEarlyInterceptors() {
  document.addEventListener(
    "click",
    function (e) {
      if (window.__fenixSystemsStatusBypass) {
        window.__fenixSystemsStatusBypass = false;
        return;
      }
      const opener = findInterceptedOpener(e.target);
      if (!opener) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      ensureIdaOkOrShowModal().then((ok) => {
        if (!ok) {
          return;
        }
        if (!window.jQuery || !jQuery.fn.modal) {
          return;
        }
        const dt = opener.getAttribute("data-target");
        if (dt) {
          jQuery(dt).modal("show");
        }
      });
    },
    true
  );
}

export function initSystemsStatus() {
  fetchSystemsStatus().then((doc) => {
    if (isIdaOkFromDoc(doc)) {
      document.body.classList.remove("fenix-ida-down");
    } else {
      document.body.classList.add("fenix-ida-down");
    }
  });
  attachEarlyInterceptors();
}
